import { ProgramType, type CohortSupportFlag, type CohortSupportFlags } from './Program.types';

/*
    The program-type flags the cohort editor reads.

    These used to live only in the program table inside `schedulable.js`, which is an Angular
    factory. The React cohort editor cannot read that table, so the values live here and
    `schedulable.js` delegates to them. One source keeps the two editors from drifting.

    They are not in `ProgramTypeConfigs` because `getProgramConfigValue` reads a plain value, and
    `supportsAutoDeclineAdmissionOffersDate` depends on the cohort as well as the program type.
    See the function at the foot of this file.
*/

// A program type with no entry in the Angular program table, such as `demo`, read every flag as
// false. Keep that, and give the groups below an explicit starting point.
const NO_SUPPORT: CohortSupportFlags = {
    supportsEnrollmentAgreement: false,
    supportsEnrollmentDeadline: false,
    supportsEnrollmentDocumentsDeadline: false,
    supportsOfficialTranscriptsDeadline: false,
    supportsSpecializations: false,
    supportsIsolatedNetwork: false,
    supportsUnlockContentAtRegistration: false,
};

// `baseDegreeProgram` in schedulable.js.
const DEGREE: CohortSupportFlags = {
    ...NO_SUPPORT,
    supportsEnrollmentAgreement: true,
    supportsEnrollmentDeadline: true,
    supportsEnrollmentDocumentsDeadline: true,
    supportsOfficialTranscriptsDeadline: true,
    supportsSpecializations: true,
};

// `baseVLACertificate` in schedulable.js. A VLA program has a graduation date, but it has its own
// ceremony and no enrollment paperwork.
const VLA: CohortSupportFlags = {
    ...NO_SUPPORT,
    supportsSpecializations: true,
};

// `baseExecEdCertificate` in schedulable.js. Note it sets no official transcripts deadline.
const EXEC_ED_CERTIFICATE: CohortSupportFlags = {
    ...NO_SUPPORT,
    supportsEnrollmentAgreement: true,
    supportsEnrollmentDeadline: true,
    supportsEnrollmentDocumentsDeadline: true,
    supportsSpecializations: true,
};

// An exec ed certificate that unlocks its content as soon as the student registers.
const EXEC_ED_UNLOCKED_AT_REGISTRATION: CohortSupportFlags = {
    ...EXEC_ED_CERTIFICATE,
    supportsUnlockContentAtRegistration: true,
};

const FLAGS_BY_PROGRAM_TYPE: Record<ProgramType, CohortSupportFlags> = {
    [ProgramType.mba]: DEGREE,
    [ProgramType.mba_leadership]: DEGREE,
    [ProgramType.msba]: DEGREE,
    [ProgramType.msse]: DEGREE,
    [ProgramType.msaie]: DEGREE,
    [ProgramType.bba]: DEGREE,

    // The two EMBA programs run an isolated student network.
    [ProgramType.emba]: { ...DEGREE, supportsIsolatedNetwork: true },
    [ProgramType.emba_strategic_leadership]: { ...DEGREE, supportsIsolatedNetwork: true },

    [ProgramType.vla]: VLA,
    [ProgramType.vla_aspiring_managers]: VLA,

    [ProgramType.tyb_ai_cert]: EXEC_ED_UNLOCKED_AT_REGISTRATION,
    [ProgramType.tyb_blockchain_cert]: EXEC_ED_UNLOCKED_AT_REGISTRATION,
    [ProgramType.prototype_gpt_cert]: EXEC_ED_UNLOCKED_AT_REGISTRATION,
    [ProgramType.ai_for_technical_leaders_cert]: EXEC_ED_UNLOCKED_AT_REGISTRATION,
    [ProgramType.ai_for_business_cert]: EXEC_ED_UNLOCKED_AT_REGISTRATION,

    [ProgramType.learn_code_gpt_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.fin_for_non_fin_managers_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.bus_analytics_leaders_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.data_science_foundations_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.cto_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.founders_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.executive_cert]: EXEC_ED_CERTIFICATE,
    [ProgramType.cmo_cert]: EXEC_ED_CERTIFICATE,

    // These two go through the certificate factory without the exec ed base, so every flag falls
    // back to its default of false.
    [ProgramType.the_business_certificate]: NO_SUPPORT,
    [ProgramType.jordanian_math]: NO_SUPPORT,

    [ProgramType.career_network_only]: NO_SUPPORT,
    [ProgramType.external]: NO_SUPPORT,
    [ProgramType.demo]: NO_SUPPORT,
};

export function getCohortSupportFlag(programType: ProgramType | undefined, flag: CohortSupportFlag): boolean {
    if (!programType) return false;
    return FLAGS_BY_PROGRAM_TYPE[programType]?.[flag] ?? false;
}

// Cycle 71 is the first cohort cycle that declines an unanswered admission offer automatically.
const AUTO_DECLINE_FIRST_START_DATE = new Date(1756713600 * 1000);

const AUTO_DECLINE_ALWAYS: ProgramType[] = [
    ProgramType.tyb_ai_cert,
    ProgramType.tyb_blockchain_cert,
    ProgramType.learn_code_gpt_cert,
    ProgramType.fin_for_non_fin_managers_cert,
    ProgramType.bus_analytics_leaders_cert,
    ProgramType.data_science_foundations_cert,
    ProgramType.prototype_gpt_cert,
    ProgramType.cto_cert,
    ProgramType.founders_cert,
    ProgramType.executive_cert,
    ProgramType.cmo_cert,
    ProgramType.ai_for_technical_leaders_cert,
    ProgramType.ai_for_business_cert,
];

const AUTO_DECLINE_FROM_CYCLE_71: ProgramType[] = [
    ProgramType.mba,
    ProgramType.mba_leadership,
    ProgramType.emba,
    ProgramType.emba_strategic_leadership,
    ProgramType.msba,
    ProgramType.msse,
    ProgramType.msaie,
];

/*
    Unlike the flags above, this one reads the cohort as well as the program type. Every master's
    program supports it from cycle 71, and every exec ed certificate always supports it. The BBA
    does not support it.
*/
export function supportsAutoDeclineAdmissionOffersDate(
    programType: ProgramType | undefined,
    startDate: Date | null | undefined,
): boolean {
    if (!programType) return false;
    if (AUTO_DECLINE_ALWAYS.includes(programType)) return true;
    if (!AUTO_DECLINE_FROM_CYCLE_71.includes(programType)) return false;

    return !!startDate && startDate >= AUTO_DECLINE_FIRST_START_DATE;
}
